import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { Insights } from './insights.service';

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {

  constructor(private swUpdate: SwUpdate, private insights: Insights) {}

  /**
   * Manually trigger an update check.
   * Returns true if a new version is available, false otherwise.
   * Throws if the service worker is not enabled (e.g. in dev mode).
   */
  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      console.warn('Service worker not enabled.');
      return false;
    }

    try {
      this.insights.trackEvent("Pre-Update Check", "Checking for app update");
      const updateFound = await this.swUpdate.checkForUpdate();
      this.insights.trackEvent("Post-Update Check", updateFound ? 'Update available.' : 'Already on latest version.');
      return updateFound;
    } catch (err) {
      console.error('Update check failed:', err);
      return false;
    }
  }

  /**
   * Activate the waiting update and reload the page.
   * Call this after the user confirms they want to update.
   */
  async activateUpdate(): Promise<void> {
    await this.swUpdate.activateUpdate();
    this.insights.trackEvent("Activate update", "Loading update");
    document.location.reload();
  }

  /**
   * One-shot helper: check and, if an update is available, ask
   * the user before reloading. Suitable for calling in AppComponent.
   */
  async checkAndPrompt(): Promise<void> {
    const available = await this.checkForUpdate();
    if (!available) return;

    // Replace with your own dialog/snackbar if preferred
    const confirmed = confirm(
      'A new version is available. Reload now to update?'
    );
    if (confirmed) await this.activateUpdate();
  }

  /**
   * Subscribe to version-ready events pushed by the SW itself
   * (background checks). Useful alongside manual checks.
   */
  listenForBackgroundUpdates(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.checkAndPrompt());
  }
}